import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import debounce from 'lodash/debounce';

// Common Components Import
import ProductTableSales from '../../../Common/SalesMProduct';
import ChargesSection from "../../../Common/ChargesSection";
import { CHARGE_OPTIONS, UNIT_OPTIONS } from "../../../Common/constants";
import CommonSalesQuotationFields from '../CommonSalesQuotationFields';

import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';

function CreateAraiMfgDomesticQuotation() {
  const navigate = useNavigate();
  const { id } = useParams();

  // Form state
  const [enquiryrefno, setenquiryrefno] = useState('');
  const [enquiryrefdate, setenquiryrefdate] = useState('');
  const [customermaterialcode, setcustomermaterialcode] = useState('');
  const [customerenquirynumber, setcustomerenquirynumber] = useState('');
  const [quotevalidity, setquotevalidity] = useState('');
  const [deliveryterm, setdeliveryterm] = useState('');
  const [quotationno, setquotationno] = useState('');
  const [quotationdate, setquotationdate] = useState('');
  const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
  const [isEnquiryValid, setIsEnquiryValid] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [commonFieldsData, setcommonFieldsData] = useState({});

  // Product and charges state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
  const [rows, setRows] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([
    {
      id: Date.now(),
      description: "",
      chargepercent: 0,
      amount: 0,
      gstPercent: 0,
      isPercentBased: true,
    },
  ]);

  const checkquotationno = async (number) => {
    if (!number) {
      setEnquiryValidationMsg('');
      setIsEnquiryValid(false);
      return;
    }

    if (!/^[A-Za-z0-9-]+$/.test(number)) {
      setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
      setIsEnquiryValid(false);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}quotation/check/${number}`, {
        headers: authHeader(),
      });

      if (response.data === true) {
        setEnquiryValidationMsg("❌ This quotation number already exists.");
        setIsEnquiryValid(false);
      } else {
        setEnquiryValidationMsg("✅ This quotation number is available.");
        setIsEnquiryValid(true);
      }
    } catch (error) {
      setEnquiryValidationMsg("❌ Error validating quotation number.");
      setIsEnquiryValid(false);
    }
  };

  useEffect(() => {
    const debouncedCheck = debounce(() => {
      checkquotationno(quotationno);
    }, 400);

    debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [quotationno]);

  const getPumpProductData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}product/page/ptype/0/10/desc/id/1/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
        { headers: authHeader() }
      );
      const products = response.data.Index || response.data.data || [];
      setProductOptions(
        products.map((product) => ({
          value: product.id,
          label: `${product.description} - ${product.moc} - ${product.size} - ${product.tagnumber} - ${product.serialnumber} - ${product.partno}`,
          description: product.description,
          uom: product.unitofmeasurement || UNIT_OPTIONS[0].value,
          igst: product.igst || 0,
          partno: product.partno,
          serialnumber: product.serialnumber,
          modelnumber: product.modelnumber,
          tagnumber: product.tagnumber,
          hsnsacno: product.hsnsacno,
          moc: product.moc || "",
          type: product.type || "",
          size: product.size || "",
          rating: product.rating || "",
          classes: product.classes || "",
          schedule: product.schedule || "",
          certification: product.certification || "",
        }))
      );
    } catch (error) {
      Swal.fire("Error", "Failed to fetch product data", "error");
    }
  };

  useEffect(() => {
    getPumpProductData();
  }, [productSearch]);

  const handleProductSearchChange = debounce((inputValue) => {
    setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
  }, 500);

  const handleProductChange = (selectedOption) => {
    setSelectedProduct(selectedOption);
    const selectedProductData = productOptions.find(
      (product) => product.value === selectedOption.value
    );

    if (selectedProductData) {
      const newRow = {
        description: selectedProductData.description,
        value: selectedProductData.value,
        label: selectedProductData.label,
        productremarks: "",
        quantity: "",
        uom: selectedProductData.uom || "",
        unitRateInr: selectedProductData.unitrateinr || 0,
        gstPercent: selectedProductData.igst || 0,
        amount: selectedProductData.unitrateinr || 0,
        totalinr: (selectedProductData.unitrateinr || 0) * (1 + (selectedProductData.igst || 0) / 100),
        hsnsacno: selectedProductData.hsnsacno,
        partno: selectedProductData.partno,
        serialnumber: selectedProductData.serialnumber,
        moc: selectedProductData.moc || "",
        type: selectedProductData.type || "",
        size: selectedProductData.size || "",
        rating: selectedProductData.rating || "",
        classes: selectedProductData.classes || "",
        schedule: selectedProductData.schedule || "",
        certification: selectedProductData.certification || "",
      };
      setRows([...rows, newRow]);
    }
  };

  // Handle row changes with 2 decimal places
  const handleRowChange = (index, key, value) => {
    const updatedRows = [...rows];
    updatedRows[index][key] = value;

    if (["quantity", "unitRateInr", "gstPercent"].includes(key)) {
      const quantity = parseFloat(updatedRows[index].quantity) || 0;
      const unitRateInr = parseFloat(updatedRows[index].unitRateInr) || 0;
      const gstPercent = parseFloat(updatedRows[index].gstPercent) || 0;

      const amount = parseFloat((quantity * unitRateInr).toFixed(2));
      const gstAmount = parseFloat(((amount * gstPercent) / 100).toFixed(2));
      const totalinr = parseFloat((amount + gstAmount).toFixed(2));

      updatedRows[index].amount = amount;
      updatedRows[index].totalinr = totalinr;
    }

    setRows(updatedRows);
  };

  // Create empty charge row
  const createEmptyCharge = () => ({
    id: Date.now(),
    description: "",
    chargepercent: 0,
    amount: 0,
    gstPercent: 0,
    isPercentBased: true,
  });

  // Handle charge changes with 2 decimal places
  const handleChargeChange = (index, field, value, isBlur = false) => {
    setAdditionalCharges(prev => {
      const newCharges = [...prev];
      const charge = { ...newCharges[index] };
      const netTotal = rows.reduce((sum, row) => sum + (row.totalinr || 0), 0);

      if (field === "chargepercent") {
        const percentage = Math.min(value, 100);
        charge.chargepercent = parseFloat(percentage.toFixed(2));

        if (isBlur || percentage === 100) {
          const calculatedAmount = (percentage * netTotal) / 100;
          charge.amount = parseFloat(calculatedAmount.toFixed(2));
        } else {
          charge.amount = 0;
        }
      }
      else if (field === "amount") {
        charge.amount = parseFloat(value.toFixed(2));
        charge.chargepercent = 0;
      }
      else if (field === "gstPercent") {
        charge.gstPercent = parseFloat(value.toFixed(2));
      }
      else {
        charge[field] = value;
      }

      newCharges[index] = charge;
      return newCharges;
    });
  };

  const addNewChargeRow = () => {
    setAdditionalCharges((prev) => [...prev, createEmptyCharge()]);
  };

  const removeChargeRow = (index) => {
    if (additionalCharges.length > 1) {
      setAdditionalCharges((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const removeRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  // Calculate products total with 2 decimal places
  const productsTotal = useMemo(() => {
    const total = rows.reduce((total, row) => {
      const rowTotal = (row.quantity || 0) * (row.unitRateInr || 0) * (1 + (row.gstPercent || 0) / 100);
      return total + rowTotal;
    }, 0);
    return parseFloat(total.toFixed(2));
  }, [rows]);

  useEffect(() => {
    // Recalculate charge amounts based on percentages when productsTotal changes
    setAdditionalCharges(prevCharges => {
      return prevCharges.map(charge => {
        if (charge.chargepercent > 0) {
          return {
            ...charge,
            amount: parseFloat(((charge.chargepercent * productsTotal) / 100).toFixed(2))
          };
        }
        return charge;
      });
    });
  }, [productsTotal]);

  // Calculate charges total with 2 decimal places
  const additionalChargesTotal = useMemo(() => {
    const total = additionalCharges.reduce((total, charge) => {
      const chargeTotal = (charge.amount || 0) * (1 + (charge.gstPercent || 0) / 100);
      return total + chargeTotal;
    }, 0);
    return parseFloat(total.toFixed(2));
  }, [additionalCharges]);

  // Calculate gross total and round to nearest integer with 2 decimal places
  const grossTotal = parseFloat((productsTotal + additionalChargesTotal).toFixed(2));
  const roundedGrossTotal = Math.round(grossTotal);
  const grandTotal = roundedGrossTotal;

  const validateForm = () => {
    if (!commonFieldsData.customer) {
      Swal.fire("Error", "Please select a customer", "error");
      return false;
    }
    if (!commonFieldsData.customerMultipleAddress) {
      Swal.fire("Error", "Please select customer address", "error");
      return false;
    }
    if (rows.length === 0) {
      Swal.fire("Error", "Please add at least one product", "error");
      return false;
    }
    return true;
  };

  const handleCommonDataChange = (data) => {
    setcommonFieldsData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const handleSubmit = async () => {
    if (!isEnquiryValid) {
      Swal.fire("Error", "Please enter a valid quotation number", "error");
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);

    // Prepare quotation items with 2 decimal places
    const quotationItems = rows.map((row) => ({
      product: { id: row.value },
      quantity: row.quantity,
      uom: row.uom,
      unitrateinr: parseFloat(row.unitRateInr.toFixed(2)),
      gst: parseFloat(row.gstPercent.toFixed(2)),
      amount: parseFloat(row.amount.toFixed(2)),
      totalinr: parseFloat(row.totalinr.toFixed(2)),
      productremarks: row.productremarks,
      partno: row.partno || "",
      hsnsacno: row.hsnsacno || "",
      serialnumber: row.serialnumber || "",
      moc: row.moc || "",
      type: row.type || "",
      size: row.size || "",
      rating: row.rating || "",
      classes: row.classes || "",
      schedule: row.schedule || "",
      certification: row.certification || "",
    }));

    // Prepare additional charges with 2 decimal places
    const additionalChargesData = additionalCharges
      .filter((charge) => charge.description && (charge.amount > 0 || charge.chargepercent > 0))
      .map((charge) => {
        const chargeAmount = parseFloat(charge.amount.toFixed(2));
        const gstPercent = parseFloat(charge.gstPercent.toFixed(2));
        const gstAmount = parseFloat((chargeAmount * gstPercent / 100).toFixed(2));
        const chargetotal = parseFloat((chargeAmount + gstAmount).toFixed(2));

        return {
          chargetype: charge.description,
          chargepercent: parseFloat(charge.chargepercent.toFixed(2)),
          chargeamount: chargeAmount,
          chargegst: gstPercent,
          chargetotal: chargetotal,
          isPercentBased: charge.isPercentBased,
        };
      });

    // Calculate totals with 2 decimal places
    const additionalChargesTotalCalc = parseFloat(additionalChargesData.reduce(
      (sum, charge) => sum + charge.chargetotal,
      0
    ).toFixed(2));

    const productsTotalCalc = parseFloat(quotationItems.reduce(
      (sum, item) => sum + item.totalinr,
      0
    ).toFixed(2));

    const grossTotalCalc = parseFloat((productsTotalCalc + additionalChargesTotalCalc).toFixed(2));

    const payload = {
      quotationItems: quotationItems,
      additionalCharges: additionalChargesData.length > 0 ? additionalChargesData : null,
      nettotalproduct: productsTotalCalc,
      additionalchargestotal: additionalChargesTotalCalc,
      grosstotal: grossTotalCalc,
      grandtotalproduct: grossTotalCalc,
      quotationtype: "Manufacturing",
      quotationcategory: "Domestic",
      quotationdate,
      quotationno,
      customermaterialcode,
      customerenquirynumber,
      quotevalidity,
      deliveryterm,
      enquiryrefno,
      enquiryrefdate,
      customerinvoicedate: commonFieldsData.customerinvoicedate,
      customerinvoiceno: commonFieldsData.customerinvoiceno,
      status: commonFieldsData.status,
      shipto: commonFieldsData.shipto,
      otherreference: commonFieldsData.otherreference,
      deliverynoteno: commonFieldsData.deliverynoteno,
      deliverynotedate: commonFieldsData.deliverynotedate,
      customer: commonFieldsData.customer ? { id: commonFieldsData.customer.value } : null,
      contactperson: commonFieldsData.customerDetails?.contactperson,
      contactno: commonFieldsData.customerDetails?.contactno,
      email: commonFieldsData.customerDetails?.email,
      customeraddress: commonFieldsData.customerMultipleAddress
        ? { id: commonFieldsData.customerMultipleAddress.value }
        : null,
      freight: commonFieldsData.customerDetails.freight,
      packingandforwarding: commonFieldsData.customerDetails.packingandforwarding,
      paymentterm: commonFieldsData.customerDetails.paymentterm,
      branch: commonFieldsData.branch ? { id: commonFieldsData.branch.value } : null,
      warehouse: commonFieldsData.warehouse ? { id: commonFieldsData.warehouse.value } : null,
    };

    console.log("Submitting payload:", payload);

    try {
      const response = await axios.post(`${BASE_URL}quotation/save`, payload, {
        headers: authHeader(),
      });
      Swal.fire("Success", "Quotation created successfully", "success");
      navigate("/quotationIndexTable/1");
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to create quotation",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Quotation Number: *</Form.Label>
              <Form.Control
                type="text"
                value={quotationno}
                onChange={e => {
                  setquotationno(e.target.value);
                  setFormSubmitted(false);
                }}
                isInvalid={
                  (formSubmitted && !isEnquiryValid) ||
                  enquiryValidationMsg.includes('exists') ||
                  enquiryValidationMsg.includes('Error') ||
                  enquiryValidationMsg.includes('allowed')
                }
                required
              />
              {enquiryValidationMsg && (
                <Form.Control.Feedback type={
                  enquiryValidationMsg.includes('exists') ||
                    enquiryValidationMsg.includes('Error') ||
                    enquiryValidationMsg.includes('allowed') ?
                    "invalid" : "valid"
                }>
                  {enquiryValidationMsg}
                </Form.Control.Feedback>
              )}
              {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid quotation number
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Quotation Date</Form.Label>
              <Form.Control
                type="date"
                value={quotationdate}
                onChange={(e) => setquotationdate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Enquiry Ref No.:</Form.Label>
              <Form.Control
                type="text"
                value={enquiryrefno}
                onChange={(e) => setenquiryrefno(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Enquiry Ref Date</Form.Label>
              <Form.Control
                type="date"
                value={enquiryrefdate}
                onChange={(e) => setenquiryrefdate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Customer Enquiry Number:</Form.Label>
              <Form.Control
                type="text"
                value={customerenquirynumber}
                onChange={(e) => setcustomerenquirynumber(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Customer Material code</Form.Label>
              <Form.Control
                type="text"
                value={customermaterialcode}
                onChange={(e) => setcustomermaterialcode(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Quote Validity</Form.Label>
              <Form.Control
                type="text"
                value={quotevalidity}
                onChange={(e) => setquotevalidity(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="enquiryno">
              <Form.Label>Delivery Term</Form.Label>
              <Form.Control
                type="text"
                value={deliveryterm}
                onChange={(e) => setdeliveryterm(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <CommonSalesQuotationFields
          initialData={commonFieldsData}
          onDataChange={handleCommonDataChange}
        />

        {/* Products Section - Common ProductTable Component Use Kiya */}
        <div className="mt-4">
          <h5>Products</h5>
          <Row>
            <Col md="12">
              <Form.Group>
                <Form.Label>Select Product</Form.Label>
                <Select
                  value={selectedProduct}
                  onChange={handleProductChange}
                  options={productOptions}
                  placeholder="Search and select product"
                  onInputChange={handleProductSearchChange}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Common ProductTable Component */}
          <ProductTableSales
            rows={rows}
            unitOptions={UNIT_OPTIONS}
            onRowChange={handleRowChange}
            onRemoveRow={removeRow}
          />
        </div>

        {/* Charges Section - Common ChargesSection Component Use Kiya */}
        <ChargesSection
          additionalCharges={additionalCharges}
          chargeOptions={CHARGE_OPTIONS}
          productsTotal={productsTotal}
          additionalChargesTotal={additionalChargesTotal}
          grossTotal={grossTotal}
          grandTotal={grandTotal}
          onChargeChange={handleChargeChange}
          onAddCharge={addNewChargeRow}
          onRemoveCharge={removeChargeRow}
        />

        <div className="d-flex justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-primary me-2"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" />
                <span className="ms-2">Submitting...</span>
              </>
            ) : (
              "Submit"
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
        </div>
      </Form>
    </div>
  );
}

export default CreateAraiMfgDomesticQuotation;
